/**
 * Clinical Print & PDF Generation Engine
 * Provides an isolated iframe print mechanism immune to browser popup blockers,
 * eliminating background screen bleed, and rendering hospital-grade A4 medical documents.
 */
import DOMPurify from "dompurify";

export const escapeHtml = (str) => {
  if (str === null || str === undefined) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

export const resolveHospitalName = (name) => {
  if (
    !name ||
    typeof name !== "string" ||
    !name.trim() ||
    name.trim().toLowerCase() === "hospital information management system" ||
    name.trim().toLowerCase() === "hmis"
  ) {
    return "Pran AI";
  }
  return name.trim();
};

export const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(num);
};

export const numberToWords = (num) => {
  const ones = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  ];
  const teens = [
    "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen",
  ];
  const tens = [
    "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety",
  ];

  const intNum = Math.floor(Math.abs(Number(num) || 0));
  if (intNum === 0) return "Zero Rupees Only";

  let words = "";
  const crores = Math.floor(intNum / 10000000);
  const lakhs = Math.floor((intNum % 10000000) / 100000);
  const thousands = Math.floor((intNum % 100000) / 1000);
  const hundreds = Math.floor((intNum % 1000) / 100);
  const remainder = intNum % 100;

  function convertTwoDigit(n) {
    if (n < 10) return ones[n];
    if (n >= 11 && n <= 19) return teens[n - 11];
    return tens[Math.floor(n / 10)] + (n % 10 > 0 ? " " + ones[n % 10] : "");
  }

  if (crores > 0) words += convertTwoDigit(crores) + " Crore ";
  if (lakhs > 0) words += convertTwoDigit(lakhs) + " Lakh ";
  if (thousands > 0) words += convertTwoDigit(thousands) + " Thousand ";
  if (hundreds > 0) words += ones[hundreds] + " Hundred ";
  if (remainder > 0) words += convertTwoDigit(remainder);

  return words.trim() + " Rupees Only";
};

export const formatDateDisplay = (dateVal) => {
  if (!dateVal) return "—";
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return String(dateVal);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const formatTimeDisplay = (dateVal) => {
  const d = dateVal ? new Date(dateVal) : new Date();
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * Base clinical stylesheet for print documents
 */
const getClinicalPrintStyles = () => `
  @page {
    size: A4 portrait;
    margin: 12mm 14mm 14mm 14mm;
  }
  * {
    box-sizing: border-box;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  html, body {
    margin: 0;
    padding: 0;
    background: #ffffff;
    color: #0f172a;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    font-size: 11px;
    line-height: 1.45;
  }
  .doc-container {
    width: 100%;
    max-width: 186mm;
    margin: 0 auto;
    color: #0f172a;
  }
  .doc-header {
    border-bottom: 2.5px solid #0284c7;
    padding-bottom: 10px;
    margin-bottom: 12px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
  }
  .hospital-branding-block {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }
  .hospital-emblem {
    width: 42px;
    height: 42px;
    flex-shrink: 0;
    margin-top: 2px;
  }
  .hospital-name {
    font-size: 18px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.2px;
    text-transform: uppercase;
    line-height: 1.15;
  }
  .hospital-tagline {
    font-size: 10.5px;
    font-weight: 700;
    color: #0284c7;
    margin-top: 2px;
    letter-spacing: 0.2px;
  }
  .hospital-meta-line {
    font-size: 9px;
    color: #475569;
    margin-top: 2px;
  }
  .hospital-dept-line {
    font-size: 10px;
    font-weight: 600;
    color: #334155;
    margin-top: 3px;
    padding-top: 2px;
    border-top: 1px dashed #cbd5e1;
  }
  .hospital-subtitle {
    font-size: 10.5px;
    color: #475569;
    margin-top: 2px;
  }
  .doc-badge {
    text-align: right;
  }
  .doc-title-badge {
    display: inline-block;
    background: #f1f5f9;
    color: #0369a1;
    border: 1px solid #bae6fd;
    padding: 4px 10px;
    border-radius: 4px;
    font-weight: 700;
    font-size: 11px;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }
  .doc-number {
    font-size: 10.5px;
    font-weight: 600;
    color: #334155;
    margin-top: 4px;
  }
  .doc-date {
    font-size: 10px;
    color: #64748b;
  }
  .meta-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 16px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 9px 12px;
    margin-bottom: 14px;
    font-size: 10.5px;
  }
  .meta-col {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .meta-row {
    display: flex;
    justify-content: space-between;
    border-bottom: 1px dashed #e2e8f0;
    padding-bottom: 2px;
  }
  .meta-row:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  .meta-label {
    color: #64748b;
    font-weight: 600;
  }
  .meta-val {
    color: #0f172a;
    font-weight: 700;
    text-align: right;
  }
  .section-heading {
    font-size: 11px;
    font-weight: 700;
    color: #0369a1;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin: 12px 0 6px 0;
    border-bottom: 1px solid #cbd5e1;
    padding-bottom: 3px;
    display: flex;
    justify-content: space-between;
  }
  table.clinical-table {
    width: 100%;
    border-collapse: collapse;
    margin: 8px 0 14px 0;
  }
  table.clinical-table th {
    background: #f1f5f9;
    color: #1e293b;
    font-size: 10px;
    font-weight: 700;
    text-align: left;
    padding: 6px 8px;
    border: 1px solid #cbd5e1;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  table.clinical-table td {
    padding: 6px 8px;
    border: 1px solid #e2e8f0;
    font-size: 10.5px;
    color: #1e293b;
    vertical-align: top;
  }
  table.clinical-table tr:nth-child(even) td {
    background: #f8fafc;
  }
  .amount-col {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  .text-center {
    text-align: center;
  }
  .totals-box {
    margin-top: 10px;
    margin-left: auto;
    width: 260px;
    background: #f8fafc;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 8px 12px;
  }
  .total-line {
    display: flex;
    justify-content: space-between;
    margin-bottom: 4px;
    font-size: 10.5px;
  }
  .total-line.grand {
    border-top: 1.5px solid #0284c7;
    padding-top: 5px;
    margin-top: 5px;
    font-size: 12px;
    font-weight: 700;
    color: #0369a1;
  }
  .words-notice {
    margin-top: 8px;
    padding: 6px 10px;
    background: #f1f5f9;
    border-left: 3px solid #0284c7;
    font-size: 10px;
    color: #334155;
    font-style: italic;
  }
  .clinical-notes-card {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 8px 12px;
    margin-bottom: 10px;
    font-size: 10.5px;
  }
  .clinical-notes-card .card-title {
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 4px;
    font-size: 10.5px;
  }
  .clinical-notes-card .card-body {
    color: #334155;
    line-height: 1.45;
  }
  .vitals-summary-bar {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    margin-bottom: 12px;
  }
  .vital-item {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    padding: 6px 8px;
    text-align: center;
  }
  .vital-item .v-label {
    font-size: 9px;
    color: #64748b;
    font-weight: 600;
    text-transform: uppercase;
  }
  .vital-item .v-val {
    font-size: 12px;
    font-weight: 700;
    color: #0f172a;
    margin-top: 2px;
  }
  .doc-footer {
    margin-top: 20px;
    padding-top: 10px;
    border-top: 1px solid #e2e8f0;
    page-break-inside: avoid;
  }
  .disclaimer-text {
    font-size: 9px;
    color: #64748b;
    line-height: 1.4;
    margin-bottom: 14px;
  }
  .sig-container {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-top: 25px;
  }
  .sig-block {
    text-align: center;
    width: 170px;
  }
  .sig-line {
    border-bottom: 1px solid #64748b;
    margin-bottom: 5px;
    height: 30px;
  }
  .sig-title {
    font-size: 9.5px;
    font-weight: 600;
    color: #1e293b;
  }
  .sig-sub {
    font-size: 8.5px;
    color: #64748b;
  }
  .badge-flag {
    display: inline-block;
    padding: 1px 5px;
    border-radius: 3px;
    font-size: 9px;
    font-weight: 700;
  }
  .flag-high { background: #fee2e2; color: #b91c1c; border: 1px solid #fca5a5; }
  .flag-low { background: #e0f2fe; color: #0369a1; border: 1px solid #7dd3fc; }
  .flag-normal { background: #dcfce7; color: #15803d; border: 1px solid #86efac; }
`;

/**
 * Print an isolated HTML string using a hidden in-page iframe
 */
export const printDocumentHtml = ({ title = "Medical Record", contentHtml }) => {
  let iframe = document.getElementById("hmis-clinical-print-iframe");
  if (!iframe) {
    iframe = document.createElement("iframe");
    iframe.id = "hmis-clinical-print-iframe";
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);
  }

  const iframeDoc = iframe.contentWindow?.document || iframe.contentDocument;
  if (!iframeDoc) {
    console.error("Unable to access print iframe document.");
    return;
  }

  const fullHtml = `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(title)}</title>
        <style>
          ${getClinicalPrintStyles()}
        </style>
      </head>
      <body>
        <div class="doc-container">
          ${contentHtml}
        </div>
      </body>
    </html>
  `;

  iframeDoc.open();
  iframeDoc.write(DOMPurify.sanitize(fullHtml, { WHOLE_DOCUMENT: true, ADD_TAGS: ["style"] }));
  iframeDoc.close();

  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      console.error("Clinical print execution error:", e);
    }
  }, 300);
};

/**
 * Common authentic medical letterhead component for printable documents
 */
export const renderHospitalHeaderHtml = ({
  hospitalName,
  departmentLine = "Clinical Services",
  badgeTitle = "DOCUMENT",
  refNumber = "",
  refLabel = "Ref No",
  dateStr = "",
  timeStr = "",
}) => {
  const resolvedName = resolveHospitalName(hospitalName);
  return `
    <div class="doc-header">
      <div class="hospital-branding-block">
        <div class="hospital-emblem">
          <svg width="42" height="42" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="44" height="44" rx="10" fill="#0284c7" />
            <rect x="18" y="10" width="8" height="24" rx="2" fill="#ffffff" />
            <rect x="10" y="18" width="24" height="8" rx="2" fill="#ffffff" />
            <circle cx="22" cy="22" r="18" stroke="#ffffff" stroke-width="1.5" stroke-opacity="0.35" fill="none" />
          </svg>
        </div>
        <div>
          <div class="hospital-name">${escapeHtml(resolvedName)}</div>
          <div class="hospital-tagline">Multi-Speciality Hospital &amp; Research Centre</div>
          <div class="hospital-meta-line">NABH Accredited • 24x7 Emergency: 108 • Ph: +91 80 4920 0000 • Web: pran.ai</div>
          <div class="hospital-dept-line">${escapeHtml(departmentLine)}</div>
        </div>
      </div>
      <div class="doc-badge">
        <div class="doc-title-badge">${escapeHtml(badgeTitle)}</div>
        ${refNumber ? `<div class="doc-number">${escapeHtml(refLabel)}: ${escapeHtml(refNumber)}</div>` : ""}
        <div class="doc-date">${escapeHtml(dateStr)}${timeStr ? ` • ${escapeHtml(timeStr)}` : ""}</div>
      </div>
    </div>
  `;
};

/**
 * Build professional Outpatient / Daycare Bill HTML
 */
export const buildInvoiceHtml = ({
  hospitalName = "Pran AI Hospital",
  hospitalAddress = "",
  invoiceData = {},
  isDaycare = false,
}) => {
  const patientName = invoiceData.fullName || invoiceData.patientData?.fullName || invoiceData.patientData?.name || "Patient";
  const uhid = invoiceData.uhid || invoiceData.patientData?.uhid || "—";
  const age = invoiceData.age || invoiceData.patientData?.calculatedAge || invoiceData.patientData?.age || "—";
  const gender = invoiceData.gender || invoiceData.patientData?.gender || "—";
  const mobile = invoiceData.mobileNumber || invoiceData.patientData?.mobile || "—";
  const abha = invoiceData.abhaNumber || invoiceData.patientData?.formattedAbhaId || invoiceData.patientData?.abhaNumber || "—";
  const billNo = invoiceData.billNumber || `BILL-${Date.now().toString().slice(-6)}`;
  const billDate = formatDateDisplay(new Date());
  const billTime = formatTimeDisplay(new Date());
  const doctor = invoiceData.doctorName || invoiceData.doctor || "Consulting Physician";
  const department = invoiceData.departmentName || invoiceData.department || (isDaycare ? "Daycare Services" : "General OPD");

  let items = [];
  if (Array.isArray(invoiceData.items) && invoiceData.items.length > 0) {
    items = invoiceData.items;
  } else if (invoiceData.additionalServices || invoiceData.consultationFee !== undefined) {
    items.push({
      particulars: "Consultation Fee",
      rate: invoiceData.consultationFee || 0,
      qty: 1,
      amount: parseFloat(invoiceData.consultationFee || 0),
    });
    if (Array.isArray(invoiceData.additionalServices)) {
      invoiceData.additionalServices.forEach((svc) => {
        items.push({
          particulars: svc.name || "Hospital Service",
          rate: svc.amount || 0,
          qty: 1,
          amount: parseFloat(svc.amount || 0),
        });
      });
    }
  }

  const totalAmount = items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

  const rowsHtml = items.map((item, idx) => `
    <tr>
      <td style="width: 30px; text-align: center;">${idx + 1}</td>
      <td>
        <div style="font-weight: 700; color: #0f172a;">${escapeHtml(item.particulars || item.name || "Service Item")}</div>
        ${item.code ? `<div style="font-size: 9.5px; color: #64748b;">SAC / Code: ${escapeHtml(item.code)}</div>` : ""}
      </td>
      <td style="width: 70px; text-align: center;">${escapeHtml(String(item.unit || item.qty || 1))}</td>
      <td class="amount-col" style="width: 100px;">${formatCurrency(item.rate || item.amount || 0)}</td>
      <td class="amount-col" style="width: 110px; font-weight: 700;">${formatCurrency(item.amount || 0)}</td>
    </tr>
  `).join("");

  return `
    ${renderHospitalHeaderHtml({
      hospitalName,
      departmentLine: isDaycare
        ? "Daycare Medical Services • Official Patient Billing Receipt"
        : "Outpatient Services (OPD) • Official Patient Billing Receipt",
      badgeTitle: isDaycare ? "DAYCARE BILL RECEIPT" : "OUTPATIENT BILL RECEIPT",
      refLabel: "Receipt No",
      refNumber: billNo,
      dateStr: billDate,
      timeStr: billTime,
    })}

    <div class="meta-grid">
      <div class="meta-col">
        <div class="meta-row"><span class="meta-label">Patient Name:</span><span class="meta-val">${escapeHtml(patientName)}</span></div>
        <div class="meta-row"><span class="meta-label">UHID:</span><span class="meta-val">${escapeHtml(uhid)}</span></div>
        <div class="meta-row"><span class="meta-label">Age / Gender:</span><span class="meta-val">${escapeHtml(age)} / ${escapeHtml(gender)}</span></div>
        <div class="meta-row"><span class="meta-label">Mobile Contact:</span><span class="meta-val">${escapeHtml(mobile)}</span></div>
      </div>
      <div class="meta-col">
        <div class="meta-row"><span class="meta-label">Consulting Doctor:</span><span class="meta-val">${escapeHtml(doctor)}</span></div>
        <div class="meta-row"><span class="meta-label">Department:</span><span class="meta-val">${escapeHtml(department)}</span></div>
        <div class="meta-row"><span class="meta-label">ABHA ID:</span><span class="meta-val">${escapeHtml(abha)}</span></div>
        <div class="meta-row"><span class="meta-label">Payment Status:</span><span class="meta-val" style="color: #15803d;">PAID</span></div>
      </div>
    </div>

    <table class="clinical-table">
      <thead>
        <tr>
          <th style="width: 30px; text-align: center;">#</th>
          <th>Service Description / Particulars</th>
          <th style="width: 70px; text-align: center;">Qty / Unit</th>
          <th class="amount-col" style="width: 100px;">Rate (₹)</th>
          <th class="amount-col" style="width: 110px;">Amount (₹)</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml || `<tr><td colspan="5" class="text-center" style="padding: 16px; color: #64748b;">No billing items recorded.</td></tr>`}
      </tbody>
    </table>

    <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-top: 8px;">
      <div style="flex: 1; max-width: 360px;">
        <div class="words-notice">
          <strong>Amount in Words:</strong><br />
          ${numberToWords(totalAmount)}
        </div>
        <div style="font-size: 9.5px; color: #64748b; margin-top: 8px;">
          Note: Clinical and healthcare services provided by registered clinical establishments are exempt from GST under standard health service notifications.
        </div>
      </div>

      <div class="totals-box">
        <div class="total-line">
          <span style="color: #64748b;">Subtotal:</span>
          <span>${formatCurrency(totalAmount)}</span>
        </div>
        <div class="total-line">
          <span style="color: #64748b;">Taxes (GST Exempt):</span>
          <span>₹0.00</span>
        </div>
        <div class="total-line grand">
          <span>Net Total Paid:</span>
          <span>${formatCurrency(totalAmount)}</span>
        </div>
      </div>
    </div>

    <div class="doc-footer">
      <div class="disclaimer-text">
        • This is a computer-generated official billing receipt issued by the hospital management system and does not require an ink seal.<br />
        • This receipt certifies payment received for diagnostic, outpatient, or daycare clinical services utilized by the patient.<br />
        • Please retain this receipt for future clinical reference, reimbursement claims, or tax declarations.
      </div>
      <div class="sig-container">
        <div class="sig-block">
          <div class="sig-line"></div>
          <div class="sig-title">Patient / Attendant Signature</div>
          <div class="sig-sub">Acknowledged Receipt</div>
        </div>
        <div class="sig-block">
          <div class="sig-line"></div>
          <div class="sig-title">Authorized Cashier / Billing Officer</div>
          <div class="sig-sub">Hospital Accounts Department</div>
        </div>
      </div>
    </div>
  `;
};

/**
 * Build professional Clinical Encounter / Visit Summary HTML
 */
export const buildVisitSummaryHtml = ({
  hospitalName = "Pran AI Hospital",
  patient = {},
  visitData = {},
  visitId = "",
}) => {
  const patientName = patient.name || patient.fullName || "Patient";
  const uhid = patient.uhid || patient.UHID || "—";
  const age = patient.age || patient.calculatedAge || "—";
  const gender = patient.gender || "—";
  const doctor = patient.doctor || visitData.doctor || "Attending Physician";
  const encounterDate = formatDateDisplay(visitData.soapNotes?.createdAt || new Date());
  const encounterTime = formatTimeDisplay(visitData.soapNotes?.createdAt || new Date());

  const vitals = visitData.vitals || {};
  const soap = visitData.soapNotes || {};
  const prescriptions = Array.isArray(visitData.prescriptions) ? visitData.prescriptions : [];
  const labResults = Array.isArray(visitData.labResults) ? visitData.labResults : [];
  const immunizations = Array.isArray(visitData.immunizations) ? visitData.immunizations : [];
  const billing = visitData.billing || null;

  const rxRows = prescriptions.map((p, i) => `
    <tr>
      <td style="text-align: center; width: 30px;">${i + 1}</td>
      <td>
        <div style="font-weight: 700; color: #0f172a;">${escapeHtml(p.medicineName || p.name || p.medicine)}</div>
        ${p.instructions ? `<div style="font-size: 9.5px; color: #64748b;">${escapeHtml(p.instructions)}</div>` : ""}
      </td>
      <td>${escapeHtml(p.dosage || p.frequency || "1 Dose")}</td>
      <td>${escapeHtml(p.duration || "5 days")}</td>
    </tr>
  `).join("");

  return `
    ${renderHospitalHeaderHtml({
      hospitalName,
      departmentLine: "Department of Outpatient Clinical Medicine • Consultation Summary",
      badgeTitle: "VISIT SUMMARY",
      refLabel: "Visit Ref",
      refNumber: visitId || `V-${Date.now().toString().slice(-6)}`,
      dateStr: encounterDate,
      timeStr: encounterTime,
    })}

    <div class="meta-grid">
      <div class="meta-col">
        <div class="meta-row"><span class="meta-label">Patient Name:</span><span class="meta-val">${escapeHtml(patientName)}</span></div>
        <div class="meta-row"><span class="meta-label">UHID:</span><span class="meta-val">${escapeHtml(uhid)}</span></div>
        <div class="meta-row"><span class="meta-label">Age / Gender:</span><span class="meta-val">${escapeHtml(age)} / ${escapeHtml(gender)}</span></div>
      </div>
      <div class="meta-col">
        <div class="meta-row"><span class="meta-label">Consulting Doctor:</span><span class="meta-val">${escapeHtml(doctor)}</span></div>
        <div class="meta-row"><span class="meta-label">Department:</span><span class="meta-val">General Outpatient (OPD)</span></div>
        <div class="meta-row"><span class="meta-label">Encounter Date:</span><span class="meta-val">${encounterDate}</span></div>
      </div>
    </div>

    ${vitals.sbp || vitals.pulse || vitals.temp ? `
      <div class="section-heading">Recorded Patient Vitals</div>
      <div class="vitals-summary-bar">
        <div class="vital-item">
          <div class="v-label">Blood Pressure</div>
          <div class="v-val">${vitals.sbp ? `${escapeHtml(vitals.sbp)}/${escapeHtml(vitals.dbp || "—")} mmHg` : "—"}</div>
        </div>
        <div class="vital-item">
          <div class="v-label">Heart / Pulse Rate</div>
          <div class="v-val">${vitals.pulse ? `${escapeHtml(vitals.pulse)} bpm` : "—"}</div>
        </div>
        <div class="vital-item">
          <div class="v-label">Body Temperature</div>
          <div class="v-val">${vitals.temp ? `${escapeHtml(vitals.temp)} °F` : "—"}</div>
        </div>
        <div class="vital-item">
          <div class="v-label">Body Mass Index</div>
          <div class="v-val">${vitals.bmi ? `${escapeHtml(vitals.bmi)}` : "—"}</div>
        </div>
      </div>
    ` : ""}

    <div class="section-heading">Clinical Evaluation & Consultation Notes</div>
    <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin-bottom: 12px;">
      <div class="clinical-notes-card">
        <div class="card-title">Chief Complaints & Symptoms</div>
        <div class="card-body">${escapeHtml(soap.subjective || soap.complaint || "None recorded.")}</div>
      </div>
      <div class="clinical-notes-card">
        <div class="card-title">Provisional / Final Diagnosis</div>
        <div class="card-body">${escapeHtml(soap.assessment || soap.diagnosis || "Under clinical evaluation.")}</div>
      </div>
      <div class="clinical-notes-card">
        <div class="card-title">Doctor's Treatment Advice</div>
        <div class="card-body">${escapeHtml(soap.plan || soap.notes || "Follow prescribed treatment regimen.")}</div>
      </div>
    </div>

    <div class="section-heading">Prescribed Medications</div>
    <table class="clinical-table">
      <thead>
        <tr>
          <th style="width: 30px; text-align: center;">#</th>
          <th>Medication Name</th>
          <th>Dosage / Frequency</th>
          <th style="width: 120px;">Duration</th>
        </tr>
      </thead>
      <tbody>
        ${rxRows || `<tr><td colspan="4" class="text-center" style="padding: 10px; color: #64748b;">No medications prescribed for this visit.</td></tr>`}
      </tbody>
    </table>

    ${labResults.length > 0 ? `
      <div class="section-heading">Diagnostic Investigations Ordered / Linked</div>
      <table class="clinical-table">
        <thead>
          <tr>
            <th style="width: 30px; text-align: center;">#</th>
            <th>Investigation Name</th>
            <th style="width: 140px;">Status</th>
          </tr>
        </thead>
        <tbody>
          ${labResults.map((r, i) => `
            <tr>
              <td style="text-align: center;">${i + 1}</td>
              <td style="font-weight: 600;">${escapeHtml(r.testName || r.name || "Diagnostic Test")}</td>
              <td><span class="badge-flag flag-${r.status === 'Completed' ? 'normal' : 'high'}">${escapeHtml(r.status || "Pending")}</span></td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    ` : ""}

    ${immunizations.length > 0 ? `
      <div class="section-heading">Immunization Administered</div>
      <table class="clinical-table">
        <thead>
          <tr>
            <th style="width: 30px; text-align: center;">#</th>
            <th>Vaccine / Immunization</th>
            <th style="width: 140px;">Date Given</th>
            <th>Manufacturer / Details</th>
          </tr>
        </thead>
        <tbody>
          ${immunizations.map((im, i) => `
            <tr>
              <td style="text-align: center;">${i + 1}</td>
              <td style="font-weight: 600;">${escapeHtml(im.name)}</td>
              <td>${formatDateDisplay(im.date)}</td>
              <td>${escapeHtml(im.details || "—")}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    ` : ""}

    ${billing ? `
      <div class="section-heading">Encounter Billing Summary</div>
      <div style="display: flex; justify-content: space-between; font-size: 10.5px; background: #f8fafc; border: 1px solid #e2e8f0; padding: 8px 12px; border-radius: 4px;">
        <div>Invoice Ref: <strong>#${escapeHtml(billing.invoiceNo || billing._id || "—")}</strong> | Payment Mode: ${escapeHtml(billing.paymentMode || "Direct")}</div>
        <div>Total Fee: <strong style="color: #0369a1;">₹${escapeHtml(billing.totalAmount || billing.total || "0.00")}</strong> (${escapeHtml(billing.status || "PAID")})</div>
      </div>
    ` : ""}

    <div class="doc-footer">
      <div class="disclaimer-text">
        • This visit summary is an official extract of the electronic health record maintained under hospital guidelines.<br />
        • Take prescribed medications strictly as indicated. If adverse reactions or emergency symptoms develop, report to the hospital emergency department immediately.<br />
        • For follow-up consultations, please bring this summary along with previous laboratory and imaging records.
      </div>
      <div class="sig-container">
        <div class="sig-block">
          <div class="sig-line"></div>
          <div class="sig-title">Patient / Attendant Signature</div>
        </div>
        <div class="sig-block">
          <div class="sig-line"></div>
          <div class="sig-title">Consulting Physician Signature & Stamp</div>
          <div class="sig-sub">Department of Clinical Medicine</div>
        </div>
      </div>
    </div>
  `;
};

/**
 * Build professional Clinical Laboratory Diagnostic Report HTML
 */
export const buildLabReportHtml = ({
  hospitalName = "Pran AI Hospital",
  report = {},
}) => {
  const reportId = report._id || `LR-${Date.now().toString().slice(-6)}`;
  const visitId = report.visitId || "—";
  const patientId = report.patientId || "—";
  const tests = Array.isArray(report.tests) ? report.tests : [];

  let testPanelsHtml = "";
  tests.forEach((test, tIdx) => {
    const params = Array.isArray(test.parameters) ? test.parameters : [];
    const paramRows = params.map((p) => {
      return `
        <tr>
          <td style="font-weight: 600; color: #0f172a;">${escapeHtml(p.parameterName || "Parameter")}</td>
          <td style="font-weight: 700; color: #0369a1;">${escapeHtml(p.parameterValue || "—")}</td>
          <td>${escapeHtml(p.unit || "—")}</td>
          <td style="color: #475569;">${escapeHtml(p.referenceRange || "—")}</td>
        </tr>
      `;
    }).join("");

    testPanelsHtml += `
      <div style="margin-top: 14px; page-break-inside: avoid;">
        <div style="background: #f1f5f9; border: 1px solid #cbd5e1; padding: 6px 10px; font-weight: 700; font-size: 11px; color: #0f172a; display: flex; justify-content: space-between; border-radius: 4px 4px 0 0;">
          <div>${tIdx + 1}. ${escapeHtml(test.displayName || test.testType || "Laboratory Panel")}</div>
          <div style="font-size: 10px; color: #0369a1; text-transform: uppercase;">${escapeHtml(test.status || "Completed")}</div>
        </div>

        <div style="display: flex; gap: 20px; font-size: 10px; color: #475569; padding: 6px 10px; border-left: 1px solid #e2e8f0; border-right: 1px solid #e2e8f0; background: #fafafa;">
          ${test.sampleId ? `<div><strong>Sample ID:</strong> ${escapeHtml(test.sampleId)}</div>` : ""}
          ${test.analystName ? `<div><strong>Analyst:</strong> ${escapeHtml(test.analystName)}</div>` : ""}
          ${test.equipmentId ? `<div><strong>Equipment:</strong> ${escapeHtml(test.equipmentId)}</div>` : ""}
          <div><strong>Report Date:</strong> ${formatDateDisplay(test.reportDate || new Date())}</div>
        </div>

        <table class="clinical-table" style="margin-top: 0; border-top: none;">
          <thead>
            <tr>
              <th>Investigation / Parameter</th>
              <th style="width: 130px;">Observed Value</th>
              <th style="width: 80px;">Units</th>
              <th style="width: 140px;">Biological Ref. Interval</th>
            </tr>
          </thead>
          <tbody>
            ${paramRows || `<tr><td colspan="4" class="text-center" style="color: #64748b; padding: 10px;">No parameters registered for this panel.</td></tr>`}
          </tbody>
        </table>

        ${test.observations ? `
          <div style="margin-top: -6px; margin-bottom: 12px; padding: 6px 10px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; font-size: 10px;">
            <strong>Pathologist Interpretation:</strong> ${escapeHtml(test.observations)}
          </div>
        ` : ""}
      </div>
    `;
  });

  return `
    ${renderHospitalHeaderHtml({
      hospitalName,
      departmentLine: "Department of Laboratory Medicine & Diagnostic Pathology",
      badgeTitle: "LABORATORY REPORT",
      refLabel: "Report No",
      refNumber: reportId,
      dateStr: formatDateDisplay(new Date()),
      timeStr: formatTimeDisplay(new Date()),
    })}

    <div class="meta-grid">
      <div class="meta-col">
        <div class="meta-row"><span class="meta-label">Patient ID:</span><span class="meta-val">${escapeHtml(patientId)}</span></div>
        <div class="meta-row"><span class="meta-label">Visit ID:</span><span class="meta-val">${escapeHtml(visitId)}</span></div>
      </div>
      <div class="meta-col">
        <div class="meta-row"><span class="meta-label">Report Status:</span><span class="meta-val" style="color: #15803d; text-transform: uppercase;">${escapeHtml(report.status || "Finalized")}</span></div>
        <div class="meta-row"><span class="meta-label">Reporting Date:</span><span class="meta-val">${formatDateDisplay(new Date())}</span></div>
      </div>
    </div>

    ${testPanelsHtml || `<div class="text-center" style="padding: 20px; color: #64748b;">No test entries found in this report.</div>`}

    <div class="doc-footer">
      <div class="disclaimer-text">
        • The clinical laboratory test results relate strictly to the specimen received and analyzed.<br />
        • Observed laboratory values should be interpreted in correlation with clinical examination findings, symptoms, and medical history.<br />
        • All automated hematology and biochemistry analyzers are calibrated in compliance with standard clinical quality assurance controls.
      </div>
      <div class="sig-container">
        <div class="sig-block">
          <div class="sig-line"></div>
          <div class="sig-title">Medical Laboratory Technologist</div>
          <div class="sig-sub">Tested & Verified</div>
        </div>
        <div class="sig-block">
          <div class="sig-line"></div>
          <div class="sig-title">Consultant Pathologist / Lab Director</div>
          <div class="sig-sub">Approved & Released</div>
        </div>
      </div>
    </div>
  `;
};

/**
 * Build professional Inpatient Discharge Summary HTML
 */
export const buildDischargeSummaryHtml = ({
  hospitalName = "Pran AI Hospital",
  patient = {},
  discharge = {},
}) => {
  const patientName = patient.fullName || patient.name || "Patient";
  const uhid = patient.uhid || patient.UHID || "—";
  const age = patient.calculatedAge || patient.age || "—";
  const gender = patient.gender || "—";
  const ward = patient.ward || discharge.ward || "General Inpatient";
  const bed = patient.bed || discharge.bed || "—";
  const admissionDate = formatDateDisplay(discharge.admissionDate || patient.admissionDate || patient.lastVisitDate || new Date());
  const dischargeDate = formatDateDisplay(discharge.dischargeDate || patient.dischargeDate || new Date());

  const meds = Array.isArray(discharge.dischargeMedications) ? discharge.dischargeMedications : [];
  const medRows = meds.map((m, i) => `
    <tr>
      <td style="text-align: center; width: 30px;">${i + 1}</td>
      <td>
        <div style="font-weight: 700; color: #0f172a;">${escapeHtml(m.medicine)}</div>
        <div style="font-size: 9.5px; color: #64748b;">Form: ${escapeHtml(m.form || "Tablet")}</div>
      </td>
      <td>${escapeHtml(m.dosage || "1 Tab")} • ${escapeHtml(m.frequency || "Once daily")}</td>
      <td>${escapeHtml(m.duration || "5")} ${escapeHtml(m.durationUnit || "days")}</td>
      <td>${escapeHtml(m.instructions || "As instructed")}</td>
    </tr>
  `).join("");

  return `
    ${renderHospitalHeaderHtml({
      hospitalName,
      departmentLine: "Department of Inpatient Medicine & Acute Care",
      badgeTitle: "DISCHARGE SUMMARY",
      refLabel: "IP Ref",
      refNumber: patient.visitId || `IPD-${Date.now().toString().slice(-6)}`,
      dateStr: `Discharged: ${dischargeDate}`,
      timeStr: "",
    })}

    <div class="meta-grid">
      <div class="meta-col">
        <div class="meta-row"><span class="meta-label">Patient Name:</span><span class="meta-val">${escapeHtml(patientName)}</span></div>
        <div class="meta-row"><span class="meta-label">UHID:</span><span class="meta-val">${escapeHtml(uhid)}</span></div>
        <div class="meta-row"><span class="meta-label">Age / Gender:</span><span class="meta-val">${escapeHtml(age)} / ${escapeHtml(gender)}</span></div>
        <div class="meta-row"><span class="meta-label">Ward / Bed:</span><span class="meta-val">${escapeHtml(ward)} / ${escapeHtml(bed)}</span></div>
      </div>
      <div class="meta-col">
        <div class="meta-row"><span class="meta-label">Admission Date:</span><span class="meta-val">${admissionDate}</span></div>
        <div class="meta-row"><span class="meta-label">Discharge Date:</span><span class="meta-val">${dischargeDate}</span></div>
        <div class="meta-row"><span class="meta-label">Discharge Status:</span><span class="meta-val" style="color: #15803d;">CLINICALLY STABLE</span></div>
        <div class="meta-row"><span class="meta-label">Treating Doctor:</span><span class="meta-val">${escapeHtml(discharge.doctorSignature || patient.doctor || "Consultant Physician")}</span></div>
      </div>
    </div>

    <div class="section-heading">Clinical Diagnosis</div>
    <div class="clinical-notes-card">
      <div class="card-title">Final & Provisional Diagnosis</div>
      <div class="card-body">${escapeHtml(discharge.diagnosis || "Under clinical documentation.")}</div>
    </div>

    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px;">
      <div class="clinical-notes-card">
        <div class="card-title">Admission Clinical Notes</div>
        <div class="card-body">${escapeHtml(discharge.admissionNotes || "Patient admitted for clinical management.")}</div>
      </div>
      <div class="clinical-notes-card">
        <div class="card-title">Clinical Course in Hospital</div>
        <div class="card-body">${escapeHtml(discharge.clinicalSummary || "Hospital stay was uneventful; patient responded favorably to management.")}</div>
      </div>
    </div>

    ${discharge.investigationsResults || discharge.treatmentGiven ? `
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px;">
        <div class="clinical-notes-card">
          <div class="card-title">Key Diagnostic & Lab Findings</div>
          <div class="card-body">${escapeHtml(discharge.investigationsResults || "Laboratory parameters monitored during admission.")}</div>
        </div>
        <div class="clinical-notes-card">
          <div class="card-title">Hospital Treatment & Procedures Given</div>
          <div class="card-body">${escapeHtml(discharge.treatmentGiven || "Supportive medical therapy and nursing care administered.")}</div>
        </div>
      </div>
    ` : ""}

    ${discharge.surgicalProcedures || discharge.surgicalNote ? `
      <div class="section-heading">Surgical & Interventional Procedures</div>
      <div class="clinical-notes-card">
        <div class="card-title">Procedures: ${escapeHtml(discharge.surgicalProcedures || "None")}</div>
        <div class="card-body">${escapeHtml(discharge.surgicalNote || "No surgical complications noted.")}</div>
      </div>
    ` : ""}

    <div class="section-heading">Discharge Medications Regimen</div>
    <table class="clinical-table">
      <thead>
        <tr>
          <th style="width: 30px; text-align: center;">#</th>
          <th>Medication Name</th>
          <th>Dosage & Frequency</th>
          <th>Duration</th>
          <th>Specific Instructions</th>
        </tr>
      </thead>
      <tbody>
        ${medRows || `<tr><td colspan="5" class="text-center" style="padding: 10px; color: #64748b;">No home medications prescribed upon discharge.</td></tr>`}
      </tbody>
    </table>

    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px;">
      <div class="clinical-notes-card">
        <div class="card-title">Condition at Discharge</div>
        <div class="card-body">${escapeHtml(discharge.conditionAtDischarge || "Patient is hemodynamically stable, afebrile, and ambulatory.")}</div>
      </div>
      <div class="clinical-notes-card">
        <div class="card-title">Follow-up Instructions & Warning Signs</div>
        <div class="card-body">${escapeHtml(discharge.followUpInstructions || "Follow up at OPD in 7 days or immediately if fever, pain, or danger signs occur.")}</div>
      </div>
    </div>

    <div class="doc-footer">
      <div class="disclaimer-text">
        • This discharge summary is an authentic clinical record summarizing the patient's hospital stay, medications, and advice.<br />
        • The patient is advised to adhere strictly to the discharge medication schedule and follow up as scheduled.<br />
        • In case of any acute medical deterioration or red-flag symptoms, report to the hospital emergency casualty immediately.
      </div>
      <div class="sig-container">
        <div class="sig-block">
          <div class="sig-line"></div>
          <div class="sig-title">Patient / Attendant Signature</div>
          <div class="sig-sub">Received & Understood Advice</div>
        </div>
        <div class="sig-block">
          <div class="sig-line"></div>
          <div class="sig-title">${escapeHtml(discharge.doctorSignature || "Treating Consultant Physician")}</div>
          <div class="sig-sub">Department of Inpatient Medicine</div>
        </div>
      </div>
    </div>
  `;
};
